import { useState, useCallback, useEffect } from 'react';
import { Box, Group, Text, ThemeIcon, Stack, Avatar } from '@mantine/core';
import { Users } from 'react-feather';
import { notifications } from '@mantine/notifications';
import type {
  CohortWithDetails,
  CohortSessionTopic,
  Session,
  Topic,
  SessionContentStatus,
} from '../../../../types';
import { TopicPill } from './TopicPill';
import { TopicSelectModal } from './TopicSelectModal';
import { ParticipantLabelDivergenceIndicator } from '../../../../components/common';
import { topicsApi } from '../../../../services/api/topics';
import { sessionNumberConfigsApi } from '../../../../services/api/sessionNumberConfigs';
import { ModalityConfigPills } from '../ModalityConfigPills';
import { BuildContentButton } from './BuildContentButton';
import { SessionContentStatusBadge } from './SessionContentStatusBadge';

interface SessionRow {
  sessionNumber: number;
  weekOfDate: Date | null;
  topicId: string | null;
  topicName: string | null;
  // MED-832: existing participant-facing override, surfaced to the modal.
  displayName: string | null;
}

interface GroupCoachingSessionsSectionProps {
  cohort: CohortWithDetails;
  sessionTopics: CohortSessionTopic[];
  sessions?: Session[];
  onTopicChange?: (sessionNumber: number, topicId: string, topicName: string) => void;
}

/**
 * Displays group coaching sessions in zero state (before groups exist)
 * Allows topic assignment per session number
 */
export function GroupCoachingSessionsSection({
  cohort,
  sessionTopics,
  sessions,
  onTopicChange,
}: GroupCoachingSessionsSectionProps) {
  const [topicModalOpened, setTopicModalOpened] = useState(false);
  const [selectedSession, setSelectedSession] = useState<SessionRow | null>(null);

  // Calculate session rows from cohort settings, using actual session dates when available
  const sessionRows: SessionRow[] = calculateSessionRows(cohort, sessionTopics, sessions);
  const sessionCount = sessionRows.length;

  // MED-1038: surface this modality's facilitation config as pills on the header.
  const groupCoachingModality = cohort.modalities?.find(
    (m) => m.modality_type === 'group_coaching',
  );

  // MED-1086 (E1.3m / AC2): the per-modality switch of e1.3l's two-switch gate
  // for the Build Content entry point (the global flag is checked inside the
  // button). Fail closed when the modality or its flag is absent.
  const modalityLiveSessionDeliveryEnabled =
    groupCoachingModality?.live_session_delivery_enabled ?? false;

  // MED-1086 (E1.3m / AC11): per-session-number content_status for the e1.3k
  // badge, fetched independently of group sessions (none exist in this zero
  // state). Keyed by session_number for O(1) lookup per row.
  const [contentStatusByNumber, setContentStatusByNumber] = useState<
    Map<number, SessionContentStatus | null>
  >(new Map());

  useEffect(() => {
    let cancelled = false;
    sessionNumberConfigsApi
      .getContentStatusBySessionNumber(cohort.id)
      .then((res) => {
        if (cancelled) return;
        setContentStatusByNumber(
          new Map(res.session_numbers.map((s) => [s.session_number, s.content_status])),
        );
      })
      .catch(() => {
        // Best-effort — the badge simply doesn't render without data.
      });
    return () => {
      cancelled = true;
    };
  }, [cohort.id]);

  const handleOpenTopicModal = (session: SessionRow) => {
    setSelectedSession(session);
    setTopicModalOpened(true);
  };

  const handleTopicSelect = useCallback(
    async (topic: Topic, displayName?: string | null) => {
      if (!selectedSession) {
        notifications.show({
          title: 'Error',
          message: 'No session selected. Please try again.',
          color: 'red',
        });
        return;
      }

      try {
        // MED-832: pass display_name through so the participant-facing override
        // is persisted alongside the topic assignment.
        await topicsApi.assignSessionTopic(
          cohort.id,
          selectedSession.sessionNumber,
          topic.id,
          displayName,
        );
        onTopicChange?.(selectedSession.sessionNumber, topic.id, topic.name);
        notifications.show({
          title: 'Topic Assigned',
          message: `"${topic.name}" assigned to Session ${selectedSession.sessionNumber}`,
          color: 'green',
        });
      } catch (error) {
        notifications.show({
          title: 'Failed to Assign Topic',
          message: error instanceof Error ? error.message : 'Please try again.',
          color: 'red',
        });
      }
    },
    [cohort.id, selectedSession, onTopicChange]
  );

  return (
    <>
      <Box
        mt="md"
        style={{
          border: '1px solid #E5E2DC',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Group
          justify="space-between"
          style={{
            backgroundColor: 'var(--medley-surface-warm, #F5F4F0)',
            padding: '16px 20px',
            borderBottom: '1px solid #E5E2DC',
          }}
          wrap="nowrap"
        >
          <Group gap="sm">
            <ThemeIcon color="gray" size={40} radius="md" variant="light">
              <Users size={20} />
            </ThemeIcon>
            <div>
              <Text fw={600} size="16px">
                Group Coaching Sessions
              </Text>
              <Text size="13px" c="dimmed" mt={4}>
                {sessionCount} session{sessionCount !== 1 ? 's' : ''} · Assign topics during design
              </Text>
            </div>
            {/* MED-1038: read-only facilitation-config pills, grouped with the title */}
            <ModalityConfigPills modality={groupCoachingModality} />
          </Group>
          <Group gap="sm">
            <Text size="xs" c="dimmed">
              Groups &amp; times assigned after matching
            </Text>
          </Group>
        </Group>

        {/* Sessions List */}
        <Box style={{ backgroundColor: '#FFFFFF' }}>
          {sessionCount > 0 ? (
            <Stack gap={0}>
              {sessionRows.map((session) => (
                <Group
                  key={session.sessionNumber}
                  justify="space-between"
                  style={(theme) => ({
                    padding: '14px 20px',
                    borderBottom: `1px solid ${theme.colors.gray[2]}`,
                  })}
                >
                  <Group gap="sm">
                    <Avatar size="sm" radius="xl" color="gray" variant="outline">
                      <Text size="xs" fw={600}>
                        {session.sessionNumber}
                      </Text>
                    </Avatar>
                    <div>
                      <Text fw={500} size="sm">
                        Session {session.sessionNumber}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {session.weekOfDate
                          ? `Week of ${formatWeekDate(session.weekOfDate)}`
                          : 'Date not set'}
                      </Text>
                    </div>
                  </Group>

                  {/* Right cluster: topic → content-status badge → Build Content
                      (rightmost), per the e1.3m Visual Direction. */}
                  <Group gap="sm" wrap="nowrap">
                    <TopicPill
                      topicName={session.topicName}
                      onClick={() => handleOpenTopicModal(session)}
                    />
                    {/* MED-1147 (AC4): read-only divergence indicator complementing
                        the edit field (the TopicSelectModal opened by the pill). The
                        CM payload already carries both the canonical topic and the
                        participant display_name (MED-832), so divergence is computed
                        client-side via the shared rule — no backend add (AC6). */}
                    <ParticipantLabelDivergenceIndicator
                      canonicalTopic={session.topicName}
                      participantDisplayName={session.displayName}
                    />
                    {/* MED-1086 (E1.3m / AC11): existing e1.3k badge, now on the
                        no-groups zero state. Renders nothing for not-started. */}
                    <SessionContentStatusBadge
                      contentStatus={contentStatusByNumber.get(session.sessionNumber)}
                    />
                    {/* MED-1086 (E1.3m / AC3, AC4): no groups yet → by-number
                        editor (no representative sessionId). */}
                    <BuildContentButton
                      cohortId={cohort.id}
                      sessionNumber={session.sessionNumber}
                      modalityLiveSessionDeliveryEnabled={modalityLiveSessionDeliveryEnabled}
                    />
                  </Group>
                </Group>
              ))}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed" ta="center" py="md" px="xl">
              Configure cohort dates to see session schedule
            </Text>
          )}
        </Box>
      </Box>

      {/* Topic Select Modal */}
      <TopicSelectModal
        opened={topicModalOpened}
        onClose={() => setTopicModalOpened(false)}
        onSelect={handleTopicSelect}
        currentTopicId={selectedSession?.topicId}
        currentDisplayName={selectedSession?.displayName ?? null}
        sessionNumber={selectedSession?.sessionNumber}
      />
    </>
  );
}

/**
 * Calculate session rows based on cohort configuration.
 * Uses actual session dates from the database when available (cohort-wide sessions
 * created during cohort setup), falling back to weekly math only when no sessions exist.
 */
function calculateSessionRows(
  cohort: CohortWithDetails,
  sessionTopics: CohortSessionTopic[],
  sessions?: Session[]
): SessionRow[] {
  // Get group coaching modality for session count
  const groupCoachingModality = cohort.modalities?.find((m) => m.modality_type === 'group_coaching');

  const sessionCount = groupCoachingModality?.session_count || 6; // Default to 6

  // Build topic lookup (handle undefined/null sessionTopics)
  const topicLookup = new Map<number, CohortSessionTopic>();
  (sessionTopics || []).forEach((t) => topicLookup.set(t.session_number, t));

  // Build session date lookup from actual cohort-wide sessions (group_id = null)
  const sessionDateLookup = new Map<number, Date>();
  if (sessions && sessions.length > 0) {
    const cohortWideSessions = sessions
      .filter((s) => !s.group_id && s.scheduled_start)
      .sort((a, b) => (a.session_number || 0) - (b.session_number || 0));
    for (const s of cohortWideSessions) {
      sessionDateLookup.set(s.session_number, new Date(s.scheduled_start!));
    }
  }

  const startDate = cohort.start_date ? new Date(cohort.start_date) : null;
  const rows: SessionRow[] = [];

  for (let i = 1; i <= sessionCount; i++) {
    // Use actual session date if available, otherwise fall back to weekly math
    let weekOfDate: Date | null = sessionDateLookup.get(i) || null;
    if (!weekOfDate && startDate) {
      weekOfDate = new Date(startDate);
      weekOfDate.setDate(weekOfDate.getDate() + (i - 1) * 7);
    }

    const topicAssignment = topicLookup.get(i);

    rows.push({
      sessionNumber: i,
      weekOfDate,
      topicId: topicAssignment?.topic_id || null,
      topicName: topicAssignment?.topic_name || null,
      // MED-832: surface the existing display_name override into the modal pre-fill.
      displayName: topicAssignment?.display_name ?? null,
    });
  }

  return rows;
}

/**
 * Format date as "Mar 17, 2025" for week display
 */
function formatWeekDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default GroupCoachingSessionsSection;
